import Collection from './collection';
import GameTree from './gameTree';
import Node from './node';
import Parser from './parser';

class Sgf {

  static appName = "Fujigoban AI";

  constructor() {
    this.collection = new Collection();
    /**
     * Current game tree indices. [0, 1, 2] means 3rd child game tree 
     * of 2nd child game tree of 1st game.
     */
    this.gameTreeIndex = [];
    /**
     * Current node index in the current game tree
     */
    this.nodeIndex = 0;
  }

  get gameTree() {
    if (this.gameTreeIndex.length < 1) {
      return undefined;
    }
    let gt = this.collection;
    let indices = this.gameTreeIndex.slice();
    while (indices.length) {
      if (!gt.children[indices[0]]) {
        console.log("Error", gt, indices);
        throw new Error(`No such children: gt.children[${indices[0]}]`);
      }
      gt = gt.children[indices.shift()];
    }
    return gt;
  }

  get node() {
    if (this.gameTree && this.gameTree.nodes) {
      const nodes = this.gameTree.nodes;
      if (nodes.length > this.nodeIndex && this.nodeIndex >= 0) {
        return nodes[this.nodeIndex];
      }
      throw new RangeError(`nodeIndex ${this.nodeIndex}`, nodes);
    }
    return undefined;
  }

  /**
   * Returns the root game tree for the current game tree.
   */
  get rootGameTree() {
    if (!this.gameTree) {
      console.warn("this.gameTree is not set.");
      if (this.collection.children.length > 0) {
        this.gameTreeIndex = [0];
      } else {
        return undefined;
      }
    }
    let gt = this.gameTree;
    while (gt.parent instanceof GameTree) {
      gt = gt.parent;
    }
    return gt;
  }

  /**
   * Returns the root game tree's first node.
   */
  get rootNode() {
    return this.rootGameTree.firstNode;
  }

  /**
   * Returns the move number (node index) in the current main tree
   * including parent.
   */
  get currentMoveNumber() {
    if (!this.gameTree || !this.node || !this.gameTree.contains(this.node)) {
      return undefined;
    }
    let gt = this.gameTree;
    let moveNumber = gt.nodes.indexOf(this.node);
    while (gt.parent instanceof GameTree) {
      gt = gt.parent;
      if (gt.nodes) {
        moveNumber += gt.nodes.length;
      }
    }
    return moveNumber;
  }

  /**
   * Returns the number of nodes in the current game tree and
   * its main parent and children tree.
   */
  get mainTreeNodesCount() {
    if (!this.gameTree && this.collection.children.length > 0) {
      this.gameTreeIndex = [0];
    }
    let gt = this.gameTree;
    let nodes = gt.nodes.length;
    while (gt.parent instanceof GameTree) {
      gt = gt.parent;
      if (gt.nodes) {
        nodes += gt.nodes.length;
      }
    }
    gt = this.gameTree;
    while (gt.hasChild()) {
      gt = gt.children[0];
      if (gt.nodes) {
        nodes += gt.nodes.length;
      }
    }
    return nodes;
  }

  get boardSize() {
    const defaultBoardSize = 19;
    const rootGT = this.rootGameTree;
    if (!rootGT) {
      return defaultBoardSize;
    }
    const rootNode = rootGT.firstNode;
    if (!rootNode || !rootNode.contains("SZ")) {
      return defaultBoardSize;
    }
    const sz = rootNode.getProperty("SZ").value;    
    return isNaN(sz) ? defaultBoardSize : parseInt(sz);
  }

  clone() {
    const sgf = new Parser().parse(this.toString());
    sgf.gameTreeIndex = this.gameTreeIndex;
    sgf.nodeIndex = this.nodeIndex;
    return sgf;
  }

  clear() {
    this.collection = new Collection();
    this.gameTreeIndex = [];
    this.nodeIndex = 0;
  }

  newGame(boardSize = 19, komi = 7.5) {
    this.clear();
    const date = new Date().toISOString().split('T')[0];
    const rootNode = new Node()
      .add("FF", 4)
      .add("GM", 1)
      .add("ST", 1)
      .add("SZ", boardSize)
      .add("KM", komi)
      .add("AP", Sgf.appName)
      .add("DT", date)
      .add("CA", "UTF-8");
    this.addGame(new GameTree().add(rootNode));
  }

  /**
   * Adds a game.
   * 
   * @param {GameTree} game 
   */
  addGame(game) {
    this.collection.children.push(game);
    this.gameTreeIndex = [this.collection.size - 1];
    this.nodeIndex = 0;
  }

  /**
   * Slices nodes of main tree from start to end and returns 
   * a new Sgf object. Variations are cut. The selected node 
   * of the Sgf is set to the last node.
   * 
   * @param {number} end 
   */
  sliceTo(end = undefined) {
    let gt = this.rootGameTree;
    let nodes = gt.nodes.slice(0, end);
    while (gt.hasChild()) {
      gt = gt.children[0];
      if (end) {
        if (end <= nodes.length) {
          break;
        } else {
          nodes = nodes.concat(gt.nodes.slice(0, end - nodes.length));
        }
      } else {
        nodes = nodes.concat(gt.nodes);
      }
    }
    const sgf = new Sgf();
    const gameTree = new GameTree();
    gameTree.nodes = nodes;
    sgf.addGame(gameTree);
    sgf.nodeIndex = nodes.length - 1;
    return sgf;
  }

  /**
   * Moves to the nth node.
   * 
   * @param {number} moveNumber 
   */
  moveTo(moveNumber) {
    const current = this.currentMoveNumber;
    if (current < moveNumber) {
      for (let i = current; i < moveNumber; i++) {
        this.nextNode();
      }
    } else {
      for (let i = current; i > moveNumber; i--) {
        this.previousNode();
      }
    }
  }

  /**
   * Moves to the last node of the main tree.
   */
  moveToLast() {
    let gt = this.rootGameTree;
    while (gt.hasChild()) {
      gt = gt.children[0];
    }
    return gt.lastNode;
  }

  hasNextNode() {
    return (this.gameTree.lastNode && this.gameTree.lastNode !== this.node)
      || (this.gameTree.hasChild() && this.gameTree.children[0].nodes.length > 0);
  }

  hasPreviousNode() {
    return (this.gameTree.firstNode && this.gameTree.firstNode !== this.node)
      || (this.gameTree.parent instanceof GameTree && this.gameTree.parent.nodes.length > 0);
  }

  nextNode() {
    if (this.nodeIndex < this.gameTree.nodes.length - 1) {
      this.nodeIndex++;
    } else if (this.gameTree.hasChild() && this.gameTree.children[0].nodes.length > 0) {
      this.gameTreeIndex.push(0);
      this.nodeIndex = 0;
    } else {
      // console.warn("No more next node.");
      return null;
    }
    return this.node;
  }

  previousNode() {
    if (this.nodeIndex > 0) {
      this.nodeIndex--;
    } else if (this.gameTree.parent && this.gameTree.parent.nodes.length > 0) {
      this.gameTreeIndex.pop();
      this.nodeIndex = this.gameTree.nodes.length - 1;
    } else {
      // console.warn("No more previous node.");
      return null;
    }
    return this.node;
  }

  get blackIsNext() {
    return this.node ? this.node.isNextBlackTurn() : true;
  }

  move(vertex) {
    if (this.blackIsNext) {
      this.moveBlack(vertex);
    } else {
      this.moveWhite(vertex);
    }
  }

  moveBlack(vertex) {
    const node = new Node().add("B", vertex);
    this.gameTree.insert(this.nodeIndex + 1, node);
    this.nodeIndex++;
  }

  moveWhite(vertex) {
    const node = new Node().add("W", vertex);
    this.gameTree.insert(this.nodeIndex + 1, node);
    this.nodeIndex++;
  }

  passBlack() {
    const node = new Node().add("B", "");
    this.gameTree.insert(this.nodeIndex + 1, node);
    this.nodeIndex++;
  }

  passWhite() {
    const node = new Node().add("W", "");
    this.gameTree.insert(this.nodeIndex + 1, node);
    this.nodeIndex++;
  }

  toString() {
    return this.collection.toString();
  }
}

export default Sgf;